import { formatValueDate } from "utils/functions";

// Fast & Furious — pure helpers for validating / shaping payloads
// (sourcing + needs steps). The source of truth for validation stays on the server; these helpers
// mirror the back-end constraints to assist input and block navigation.

// --- Declaration window (5-week carousel, GET /declaration/window) ---
// States of a cell (cf. MR back !1008). Only one OPEN cell at a time:
// it is the only week actually declarable (sequential catch-up).
export const WEEK_STATES = {
  SUBMITTED: "SUBMITTED",
  DRAFT: "DRAFT",
  OPEN: "OPEN",
  BLOCKED: "BLOCKED",
  LOCKED: "LOCKED",
};

export const findOpenWeek = (weeks) =>
  (weeks || []).find((w) => w.state === WEEK_STATES.OPEN) || null;

// "YYYY-MM-DD" read as a LOCAL date: parsing it as UTC shifts it by a day.
export const parseWeekDate = (iso) => {
  const [y, m, d] = (iso || "").split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

// [current − 2 … current + 2], dated by the server. The past side is cut at
// the F&F go-live week (back FAF_GO_LIVE_MONDAY): 3 to 5 cells, always ending
// 2 weeks after the current one.
const WINDOW_PAST = 2;
const WINDOW_FUTURE = 2;
const MIN_WINDOW_SIZE = WINDOW_FUTURE + 1;
const MAX_WINDOW_SIZE = WINDOW_PAST + WINDOW_FUTURE + 1;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
// A draft that was never submitted still counts as not declared.
const LATE_WEEK_STATES = new Set([
  WEEK_STATES.OPEN,
  WEEK_STATES.BLOCKED,
  WEEK_STATES.DRAFT,
]);

// 3 to 5 dated cells, the only shape the late-week rule can trust.
export const isValidDeclarationWindow = (declarationWindow) => {
  const weeks = declarationWindow?.weeks;
  return (
    Array.isArray(weeks) &&
    weeks.length >= MIN_WINDOW_SIZE &&
    weeks.length <= MAX_WINDOW_SIZE &&
    weeks.every((w) => ISO_DATE.test(w?.week_start_date ?? ""))
  );
};

// Past weeks still to declare, oldest first; current week = 2 cells before the
// last one (server date, not browser clock).
export const getLateWeeks = (declarationWindow) => {
  if (!isValidDeclarationWindow(declarationWindow)) return [];
  const sorted = [...declarationWindow.weeks].sort((a, b) =>
    a.week_start_date.localeCompare(b.week_start_date)
  );
  const currentWeek = sorted[sorted.length - 1 - WINDOW_FUTURE].week_start_date;
  return sorted
    .filter(
      (w) =>
        w.week_start_date < currentWeek && LATE_WEEK_STATES.has(w.state)
    )
    .map((w) => w.week_start_date);
};

// « 05/10/2026, du 12/10/2026 et du 19/10/2026 », in the user's date format,
// with the translated separators.
export const joinWeekDates = ({ isoDates, separator, lastSeparator, lang }) => {
  const dates = (isoDates || []).map((iso) =>
    formatValueDate({ date: parseWeekDate(iso), lang })
  );
  if (dates.length < 2) return dates.join("");
  return `${dates.slice(0, -1).join(separator)}${lastSeparator}${dates.at(-1)}`;
};

// « 18–24 août »: a week's range, in the format the carousel cells used. Pulled
// out of the step component so the strip and the card header show the same.
export const formatWeekRange = ({ isoDate, locale }) => {
  if (!isoDate) return "";
  const start = parseWeekDate(isoDate);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return `${start.toLocaleDateString(locale, {
    day: "2-digit",
  })}–${end.toLocaleDateString(locale, { day: "2-digit", month: "short" })}`;
};

export const MIN_NEED_DASH_LINES = 5;
export const MIN_NEED_WORDS = 50;

export const countDashLines = (text) =>
  (text || "").split("\n").filter((line) => line.trim().startsWith("-")).length;

// Word = run of letters or digits; unlike missionHelper.countWords, bullet dashes never count.
export const countWords = (text) =>
  ((text || "").match(/[\p{L}\p{M}\p{N}]+/gu) || []).length;

export const isDescriptionValid = (text) =>
  countDashLines(text) >= MIN_NEED_DASH_LINES &&
  countWords(text) >= MIN_NEED_WORDS;

export const isProspectValid = (p) =>
  !!(p.first_name && p.last_name && p.company && p.prospection_date);

export const toProspectPayload = (p) => ({
  last_name: p.last_name,
  first_name: p.first_name,
  company: p.company,
  prospection_date: p.prospection_date,
  linkedin_url: p.linkedin_url || null,
});

// Per-field errors of a need: single source for both per-field display AND for
// isNeedValid, so a rule cannot drift between the aggregated and per-field validation.
export const needFieldErrors = (n) => ({
  client: !(n.client_id || n.client_name_freetext),
  category_id: !n.category_id,
  job_id: !n.job_id,
  title: !(n.title || "").trim(),
  description: !isDescriptionValid(n.description),
});

// A need carries a JOB (redmine 12940); its category follows from that job and is only
// asked for to narrow the job select. So category_id does not factor into the aggregated
// validity — job_id implies it — and is present in the map only to flag the category
// select in the display.
export const isNeedValid = (n) => {
  const e = needFieldErrors(n);
  return !(e.client || e.job_id || e.title || e.description);
};

// Splits a comma-separated keyword input into clean tokens.
export const splitKeywords = (value) =>
  (value || "")
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean);

export const toNeedPayload = (n, declarationId) => {
  const keywords = splitKeywords(n.keywords);
  return {
    // client_id (attached client) XOR client_name_freetext (free-text name entered) — the
    // client field guarantees exclusivity (selecting clears the free text, and vice versa).
    client_id: n.client_id || null,
    client_name_freetext: n.client_id ? null : n.client_name_freetext || null,
    // A need carries a JOB (redmine 12940). `category_id` stays on the form
    // side: it only narrows the job list, it is not sent.
    job_id: n.job_id,
    title: n.title,
    description: n.description,
    keywords: keywords.length ? keywords : null,
    declaration_id: declarationId ?? null,
  };
};

// Blank editable form of a need (NeedForm): single source shared by the needs step,
// the on-the-fly need creation from a qualif, and the reconstruction of a resumed need.
// `uid`: stable React key, posted at creation so a delete in the middle of the list does
// not make React reuse the wrong instance (child input/option state would bleed across cards).
export const emptyNeed = () => ({
  uid: crypto.randomUUID(),
  client_id: null,
  client_label: null,
  client_name_freetext: null,
  category_id: null,
  job_id: null,
  title: "",
  description: "",
  keywords: "",
});

// A need form is "empty" (untouched): no meaningful field filled. Used to decide
// whether an auto-forced on-the-fly creation can safely fall back to the select.
export const isNeedEmpty = (n) =>
  !n ||
  (!n.client_id &&
    !n.client_name_freetext &&
    !n.job_id &&
    !(n.title || "").trim() &&
    !(n.description || "").trim() &&
    !(n.keywords || "").trim());

// Rebuilds the editable form of a need (NeedForm) from the server need, to
// re-display a need already created under the current declaration as "being added"
// (resuming a draft). `getJobCategoryId` resolves the category from the job, since the
// server need only carries the job. NB: the server need does not carry the reference
// client's label (client_label thus stays empty for a client_id; the free text, however,
// is its own label).
export const toNeedForm = (need, getJobCategoryId = () => null) => {
  return {
    // Stable React key derived from the server id (see emptyNeed): a resumed need
    // keeps a constant key across re-renders / sibling deletions.
    uid: `srv-${need.id}`,
    id: need.id,
    client_id: need.client_id ?? null,
    client_label: null,
    client_name_freetext: need.client_name_freetext ?? null,
    category_id: getJobCategoryId(need.job_id) ?? null,
    job_id: need.job_id ?? null,
    title: need.title ?? "",
    description: need.description ?? "",
    keywords: (need.keywords || []).join(", "),
  };
};

// --- Qualifications (step 6) ---
// Wire value "Q2" means "requalified with the same candidate"; the server derives the rank.
export const QUALIF_STATUS_REQUALIF = "Q2";
export const QUALIF_STATUS_NEW_CANDIDATE = "NEW_CANDIDATE";
export const QUALIF_STATUSES = [
  "REA",
  QUALIF_STATUS_REQUALIF,
  "GO",
  "KO",
  QUALIF_STATUS_NEW_CANDIDATE,
];

// Rank the next requalification reaches: Q2 while none is reached, no upper bound.
export const nextQualifRank = (rank) => (rank ?? 1) + 1;

// A candidate always comes from a DoYouBuzz profile, which always carries an e-mail.
export const hasCandidate = (candidate) => !!candidate?.email;

// Review field errors, shared by the card, the submit alert and validity.
export const qualifReviewFieldErrors = (review) => ({
  status: !QUALIF_STATUSES.includes(review?.status),
  qualification_date:
    review?.status === QUALIF_STATUS_REQUALIF && !review.qualification_date,
  new_candidate:
    review?.status === QUALIF_STATUS_NEW_CANDIDATE &&
    !hasCandidate(review.new_candidate),
});

export const hasFieldError = (errors) => Object.values(errors).some(Boolean);

export const isQualifReviewValid = (review) =>
  !hasFieldError(qualifReviewFieldErrors(review));

// Candidate sent to the back: the DoYouBuzz id stays client-side.
const toCandidatePayload = ({ first_name, last_name, email }) => ({
  first_name,
  last_name,
  email,
});

// PATCH body of a review: the date and the candidate only travel with their own status.
export const toQualifReviewPayload = (qualifId, review) => ({
  qualif_id: qualifId,
  status: review.status,
  qualification_date:
    review.status === QUALIF_STATUS_REQUALIF ? review.qualification_date : null,
  new_candidate:
    review.status === QUALIF_STATUS_NEW_CANDIDATE
      ? toCandidatePayload(review.new_candidate)
      : null,
});

// « 22/09 » (FR) / « 09/22 » (EN): the short date of a qualif's progress hints.
export const formatDayMonth = ({ isoDate, locale }) =>
  isoDate
    ? parseWeekDate(isoDate).toLocaleDateString(locale, {
        day: "2-digit",
        month: "2-digit",
      })
    : "";

// DOM id of a review's rank date, shared by the field and the submit-time focus.
export const qualifDateInputId = (qualifId) => `faf-qualif-date-${qualifId}`;

// Same, for a new qualif: keyed by index, it has no server id yet.
export const newQualifDateInputId = (index) => `faf-qualif-new-date-${index}`;

// Dormant qualifs (next date after the declared week) need no answer and never block.
export const isQualifReviewPending = ({ qualif }, statusMap) =>
  !qualif.is_dormant && !isQualifReviewValid(statusMap[qualif.id]);

// The candidate of a new qualif, in the shape FafCandidateSearch works with.
export const newQualifCandidate = (q) =>
  q.candidate_email
    ? {
        dyb_id: q.candidate_dyb_id ?? null,
        first_name: q.candidate_first_name,
        last_name: q.candidate_last_name,
        email: q.candidate_email,
      }
    : null;

// Inverse of newQualifCandidate: a picked (or cleared) candidate as new-qualif fields.
export const candidateToQualifFields = (candidate) => ({
  candidate_dyb_id: candidate?.dyb_id ?? null,
  candidate_first_name: candidate?.first_name ?? "",
  candidate_last_name: candidate?.last_name ?? "",
  candidate_email: candidate?.email ?? "",
});

// Per-field errors of a new qualif: single source for the fields, the submit alert AND validity.
// A created qualif is never re-validated as a creation: syncQualifs POSTs nothing for it and
// there is no update route, so a draft holding one created before this field existed would
// otherwise block step 6 for good.
export const qualifFieldErrors = (q) =>
  q.id
    ? { candidate: false, need_ref: false, qualification_date: false }
    : {
        candidate: !hasCandidate(newQualifCandidate(q)),
        need_ref: !(q.use_new_need ? isNeedValid(q.new_need) : q.need_id),
        qualification_date: !q.qualification_date,
      };

// Back-end error code for the strict needs-per-declaration cap, to target the UI message.
export const NEED_WEEKLY_LIMIT_ERROR = "need_weekly_limit_reached";

// Back-end error code: deletion refused because the need has dependencies
// (attached qualif / review). Used to display a dedicated message rather than the generic one.
export const NEED_HAS_DEPENDENCIES_ERROR = "need_has_dependencies";

// Back-end error code: deletion refused because the qualif has a dependency (review).
export const QUALIF_HAS_DEPENDENCIES_ERROR = "qualif_has_dependencies";

export const toNewQualifPayload = (q, declarationId) => ({
  candidate_first_name: q.candidate_first_name,
  candidate_last_name: q.candidate_last_name,
  candidate_email: q.candidate_email,
  // Mandatory on the back since the qualif date drives the review ranks.
  qualification_date: q.qualification_date,
  need_id: q.use_new_need ? null : q.need_id,
  new_need: q.use_new_need ? toNeedPayload(q.new_need, declarationId) : null,
  // Current weekly declaration — mandatory (like the need); in form B,
  // the new need shares this same declaration (via toNeedPayload above).
  declaration_id: declarationId ?? null,
});

// Rebuilds the editable form of a qualif (QualifForm) from the server qualif, to
// re-display a qualif already created under the current declaration as "being added"
// (resuming a draft). A qualif created via the wizard always references an existing need
// (need_id), so no "new need" reconstruction (use_new_need = false).
export const toQualifForm = (qualif) => ({
  uid: `srv-${qualif.id}`,
  id: qualif.id,
  candidate_first_name: qualif.candidate_first_name ?? "",
  candidate_last_name: qualif.candidate_last_name ?? "",
  candidate_email: qualif.candidate_email ?? "",
  qualification_date: qualif.qualification_date ?? "",
  use_new_need: false,
  need_id: qualif.need_id ?? null,
  new_need: emptyNeed(),
});
